import { useState } from "react";

import {
  Callout,
  Colors,
  FormGroup,
  InputGroup,
  Portal,
} from "@blueprintjs/core";

import { Box } from "@/components/layout/flexbox";
import { BackDrop } from "@/components/pieces/BackDrop";
import { AdditionalInfoForm } from "@/components/pieces/coaching/AdditionalInfoForm";
import { DialogHeader } from "@/components/pieces/dialog/DialogHeader";
import { ScrollableModal } from "@/components/pieces/ScrollableModal";
import { getUser } from "@/components/session/JwtTokenManager";
import { PORTAL_Z_INDEX } from "@/css/constants";
import { ServiceDetailQuery, useSubmitRfqMutation } from "@/graphql";

import { BigButton, Subheading } from "./styles";

type Product = NonNullable<
  ServiceDetailQuery["coachBySlug"]
>["coachProduct"]["product"];

interface RFQModalProps {
  product: Product;
  coachId?: string;
  visible: boolean;
  onClose: () => void;
}

export function RFQModal({
  product,
  coachId,
  visible,
  onClose,
}: RFQModalProps) {
  const user = getUser();
  const [error, setError] = useState<string>();
  const [success, setSuccess] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [fieldValidatorResult, setFieldValidatorResult] = useState<
    Record<string, boolean>
  >({});

  const formFields = product?.activeFormFields ?? [];
  const activeFormFields = formFields.filter(
    (field) => field.answerType !== "BOOK_MEETING",
  );
  const isAllRequiredFieldsFilled = activeFormFields
    .filter((formField) => formField.required)
    .every((formField) => !!fieldValidatorResult[formField.id]);

  const [submitRFQ] = useSubmitRfqMutation({
    onCompleted: (data) => {
      const { error } = data?.submitRfq ?? {};
      if (error) {
        setError(error ?? undefined);
      } else {
        setSuccess(true);
      }
      setSubmitting(false);
    },
    onError: (error) => {
      setError(error.message ?? undefined);
      setSubmitting(false);
    },
  });

  const submit = (formData: Record<string, string>) => {
    setError(undefined);
    submitRFQ({
      variables: {
        productId: product.id,
        coachId: coachId ?? "",
        formData: formData,
        name,
        email,
      },
    });
  };

  // Helper: Check if all required fields are filled
  const onValuesChange = (fieldId: string, hasValue: boolean) => {
    setFieldValidatorResult((prevData) => ({
      ...prevData,
      [fieldId]: hasValue,
    }));
  };

  let content;
  if (success) {
    content = (
      <>
        <Box mb={24} fontSize={14}>
          <Callout intent="success">
            Thanks! We will email you within one business day with more
            information.
          </Callout>
        </Box>
        <BigButton intent="primary" fill={true} large={true} onClick={onClose}>
          Close
        </BigButton>
      </>
    );
  } else {
    content = (
      <>
        <Box mb={24} fontSize={14}>
          <Callout intent="primary">
            We will email you within one business day
          </Callout>
        </Box>
        {user ? null : (
          <>
            <FormGroup
              label={<strong>Your Name</strong>}
              labelInfo={<span style={{ color: Colors.RED3 }}>(required)</span>}
            >
              <InputGroup
                value={name}
                onChange={(e) => {
                  setName(e.currentTarget.value);
                }}
                disabled={submitting}
                large={true}
                fill={true}
              />
            </FormGroup>
            <FormGroup
              label={<strong>Your Email</strong>}
              labelInfo={<span style={{ color: Colors.RED3 }}>(required)</span>}
            >
              <InputGroup
                value={email}
                onChange={(e) => {
                  setEmail(e.currentTarget.value);
                }}
                disabled={submitting}
                large={true}
                fill={true}
              />
            </FormGroup>
          </>
        )}
        <AdditionalInfoForm
          formFields={activeFormFields}
          submitting={submitting}
          onSubmitReady={submit}
          onSubmitError={() => setSubmitting(false)}
          onValueChange={onValuesChange}
        />
        {error ? (
          <Box mt={24}>
            <Callout intent="danger">{error}</Callout>
          </Box>
        ) : null}
        <Box mt={24}>
          <BigButton
            intent="primary"
            fill={true}
            large={true}
            loading={submitting}
            onClick={() => setSubmitting(true)}
            disabled={!isAllRequiredFieldsFilled || submitting}
          >
            Submit Request
          </BigButton>
        </Box>
      </>
    );
  }

  return (
    <Portal>
      <BackDrop visible={visible} zIndex={PORTAL_Z_INDEX} opacity={0.6} />
      <ScrollableModal
        visible={visible}
        onClose={onClose}
        verticallyCenter={true}
        canOutsideClickClose={false}
        style={{
          position: "absolute",
          top: 24,
          left: 0,
          width: "100%",
          overflowY: "auto",
          boxSizing: "border-box",
        }}
      >
        <Box
          backgroundColor="#FFF"
          className="round-corners"
          width={550}
          maxWidth="100%"
          margin="0 auto"
        >
          <DialogHeader onClose={onClose}>
            <Subheading>Request a Quote</Subheading>
          </DialogHeader>
          <Box p={24}>{content}</Box>
        </Box>
      </ScrollableModal>
    </Portal>
  );
}
